import { Grid } from "@mui/material";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ApiResponse } from "src/apis";
import { useProfile } from "src/auth/Auth";
import { getGridFilterReferenceType } from "src/common_constants/GridFilterReferenceTypes";
import { useSnackbar } from "src/context/ShowMessage";
import DataTable, {
  DataTableColumnProps,
} from "src/shared_components/Table/DataTable";
import { FormFieldProps } from "src/shared_components/interfaces";
import * as Yup from "yup";
import CustomLoader from "./CustomLoader";
import { useAuth } from "src/auth/AuthContext";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  clearAppliedFiltersForModule,
  selectAppliedSearchFilters,
  selectAppliedTableFilters,
  setSearchFilters as persistSearchFilters,
  setTableFilters as persistTableFilters,
} from "src/redux/slices/appliedFiltersSlice";

const SearchBar = lazy(() => import("./HeaderSearch"));

const removeEmptyValues = (filters: any = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0),
    ),
  );

const normalizeTableFilters = (filters: any = {}) => {
  const { pageNumber, pageSize, deletedId, ...filterValues } = filters;
  return removeEmptyValues(filterValues);
};

const areFiltersEqual = (first: any, second: any) =>
  JSON.stringify(first) === JSON.stringify(second);

interface GridPageWrapperProps {
  label: string;
  icon?: ReactNode;
  redirectPath?: string | Function;
  columns: DataTableColumnProps[];
  heading: string;
  getData: Function;
  getDataWithClientId?: boolean;
  restoreData?: Function;
  searchLabel?: string;
  permissionCreate?: number;
  permissionUpdate?: number;
  permissionDeleteRestore?: number;
  resetData?: Object;
  disableColumnCustomization?: boolean;
  deleteData?: Function;
  module: string;
  searchFieldColumns?: FormFieldProps[];
  searchSchema?: Yup.AnyObjectSchema;
  defaultSortBy: string | null;
  sortDirection?: string;
  disableRowOnClick?: boolean;
  onViewRow?: Function;
  forSearchFieldFetchData?: Function;
  isSearchFieldNeeded?: boolean;
  id?: number;
  isHeadingNeeded?: boolean;
  isShowActionsNeeded?: boolean;
  dependency?: any;
  isApiFilter?: boolean;
  isWithIconTitle?: boolean;
  disableView?: boolean;
  fetchPDF?: Function;
  // sending email
  sendEmail?: Function;
  getEmailTemplate?: (id: number) => void;
  customDeleteMessage?: string;
  isHeaderSticky?: boolean;
  filterReferenceType?: string;
  isLongTitle?: boolean;
  persistFilters?: boolean;
}

interface TotalCounts {
  rows: number;
  pages: number;
}

const GridPageWrapper: React.FC<GridPageWrapperProps> = ({
  label = "Add New",
  redirectPath,
  heading = "",
  columns,
  getData,
  getDataWithClientId,
  restoreData,
  searchLabel,
  permissionCreate,
  permissionDeleteRestore,
  permissionUpdate,
  resetData,
  searchFieldColumns,
  searchSchema,
  disableColumnCustomization = false,
  deleteData,
  defaultSortBy,
  sortDirection,
  module = "user",
  disableRowOnClick,
  onViewRow,
  forSearchFieldFetchData,
  isSearchFieldNeeded = true,
  id,
  isHeadingNeeded = true,
  isShowActionsNeeded,
  dependency,
  isApiFilter = true,
  isWithIconTitle = false,
  disableView = false,
  fetchPDF,
  sendEmail,
  getEmailTemplate,
  customDeleteMessage,
  isHeaderSticky,
  filterReferenceType,
  isLongTitle = false,
  persistFilters = false,
}) => {
  const { displayMessage } = useSnackbar();
  const auth = useAuth();
  const { clientId } = useProfile();
  const location = useLocation();
  const initialStates = useMemo(
    () =>
      location.state && typeof location.state === "object"
        ? location.state
        : {},
    [location.state],
  );
  const routeStateFilters = useMemo(
    () => removeEmptyValues(initialStates),
    [initialStates],
  );
  const routeStateFilterKey = useMemo(
    () => JSON.stringify(routeStateFilters),
    [routeStateFilters],
  );
  const hasRouteStateFilters = routeStateFilterKey !== "{}";
  const dispatch = useAppDispatch();
  const persistedTableFilters = useAppSelector((state) =>
    persistFilters ? selectAppliedTableFilters(state, module) : undefined,
  );
  const persistedSearchFilters = useAppSelector((state) =>
    persistFilters ? selectAppliedSearchFilters(state, module) : undefined,
  );
  const [data, setData] = useState<any>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [totalCounts, setTotalCounts] = useState<TotalCounts>({
    rows: 0,
    pages: 0,
  });

  const defaultTableFilters = useMemo(
    () => ({
      sortBy: defaultSortBy,
      sortDirection: sortDirection || "asc",
      pageNumber: 1,
      pageSize: 10,
    }),
    [defaultSortBy, sortDirection],
  );

  const defaultSearchFilters = useMemo(
    () => ({
      textSearch: "",
      ...routeStateFilters,
    }),
    [routeStateFilters],
  );

  const [filters, setFilters] = useState<any>(() => ({
    sortBy: defaultSortBy,
    sortDirection: sortDirection || "asc",
    pageNumber: 1,
    pageSize: 10,
    ...(persistFilters && !hasRouteStateFilters
      ? persistedTableFilters || {}
      : {}),
  }));

  const [searchFilters, setSearchFilters] = useState<any>(() => ({
    textSearch: "",
    ...(persistFilters && !hasRouteStateFilters
      ? persistedSearchFilters || {}
      : {}),
    ...routeStateFilters,
  }));
  const searchFilterReferenceType =
    filterReferenceType || getGridFilterReferenceType(module, heading);

  const hasAppliedFilters =
    persistFilters &&
    (!areFiltersEqual(
      normalizeTableFilters(filters),
      normalizeTableFilters(defaultTableFilters),
    ) ||
      !areFiltersEqual(
        removeEmptyValues(searchFilters),
        removeEmptyValues(defaultSearchFilters),
      ));

  const resetAppliedFilters = useCallback(() => {
    if (!persistFilters) return;

    dispatch(clearAppliedFiltersForModule(module));
    setFilters(defaultTableFilters);
    setSearchFilters(defaultSearchFilters);
  }, [
    defaultSearchFilters,
    defaultTableFilters,
    dispatch,
    module,
    persistFilters,
  ]);

  useEffect(() => {
    if (!hasRouteStateFilters) return;

    if (persistFilters) {
      dispatch(clearAppliedFiltersForModule(module));
    }

    setFilters(defaultTableFilters);
    setSearchFilters(defaultSearchFilters);
  }, [
    defaultSearchFilters,
    defaultTableFilters,
    dispatch,
    hasRouteStateFilters,
    module,
    persistFilters,
    routeStateFilterKey,
  ]);

  useEffect(() => {
    if (!persistFilters || hasRouteStateFilters) return;
    dispatch(persistTableFilters({ module, filters }));
  }, [dispatch, filters, hasRouteStateFilters, module, persistFilters]);

  useEffect(() => {
    if (!persistFilters || hasRouteStateFilters) return;
    dispatch(persistSearchFilters({ module, filters: searchFilters }));
  }, [
    dispatch,
    hasRouteStateFilters,
    module,
    persistFilters,
    searchFilters,
  ]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      let response: ApiResponse = { status: 400, data: null };
      const filterData = { ...searchFilters, ...filters };
      if (getDataWithClientId) {
        response = !id
          ? await getData(`${clientId}`, filterData)
          : await getData(id);
      } else {
        response = await getData(filterData);
      }

      if (response?.status === 200) {
        if (response?.data?.records) {
          setData(response.data.records);
          setTotalCounts({
            rows: response.data.totalRecords,
            pages: response.data.totalPages,
          });
        } else if (Array.isArray(response?.data)) {
          setData(response?.data);
          setTotalCounts({
            rows: response?.data.length,
            pages: Math.ceil(response.data.length / filters.pageSize),
          });
        }
      } else if (response?.status === 401) {
        auth.signOut();
      } else {
        displayMessage(response?.data, "error");
      }
    } catch (err) {
      displayMessage("Failed to fecth data.", "error");
    } finally {
      setDataLoading(false);
    }
  }, [
    auth,
    clientId,
    dependency,
    displayMessage,
    filters,
    getData,
    getDataWithClientId,
    id,
    searchFilters,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Grid container spacing={3} mt={-5}>
      <Grid item xs={12}>
        <Suspense fallback={<CustomLoader />}>
          <SearchBar
            title={searchLabel}
            filters={searchFilters}
            setFilters={setSearchFilters}
            setPageNumber={setFilters}
            advanceSearch={searchFieldColumns}
            schema={searchSchema}
            fetchData={forSearchFieldFetchData}
            isHeadingNeeded={isHeadingNeeded}
            heading={heading}
            module={module}
            filterReferenceType={searchFilterReferenceType}
            label={label}
            redirectPath={redirectPath}
            permission={permissionCreate || 0}
            isSearchFieldNeeded={isSearchFieldNeeded}
            isWithIconTitle={isWithIconTitle}
            isLongTitle={isLongTitle}
            showAppliedFilterReset={hasAppliedFilters}
            onResetAppliedFilters={resetAppliedFilters}
          />
        </Suspense>
      </Grid>

      <Grid item xs={12}>
        <DataTable
          defaultColumns={columns}
          rows={data}
          filters={{ ...filters, ...searchFilters }}
          setFilters={setFilters}
          totalRowCount={totalCounts?.rows}
          moduleName={module}
          restoreData={restoreData}
          deleteData={deleteData}
          fetchData={fetchData}
          totalPageCount={totalCounts?.pages}
          loading={dataLoading}
          permissionUpdate={permissionUpdate}
          permissionDeleteRestore={permissionDeleteRestore}
          resetData={resetData}
          disableColumnCustomization={disableColumnCustomization}
          disableRowOnClick={!!disableRowOnClick}
          onViewRow={onViewRow}
          isApiFilter={isApiFilter ?? true}
          isShowActionsNeeded={isShowActionsNeeded}
          fetchPDF={fetchPDF}
          sendEmail={sendEmail}
          getEmailTemplate={getEmailTemplate}
          customDeleteMessage={customDeleteMessage}
          isHeaderSticky={isHeaderSticky}
        />
      </Grid>
    </Grid>
  );
};

export default GridPageWrapper;
